<template>
  <BlBackdrop :title="title" :description="description" label="Create" :submitLoading="submitLoading"
    :disabled="isDisabledSubmit" @backdrop-close="$emit('close-modal')" @backdrop-action="onSubmit">
    <div class="flex flex-col gap-5 px-8" :class="{ 'overflow-y-auto': typeInput }"
      :style="{ width: '48rem', maxHeight: 'calc(100vh - 14rem)' }">
      <div class="grid items-center grid-cols-6 gap-4 py-4 target-details">
        <div class="col-span-6">
          <BlTextSubtitle1 text="Details" />
        </div>
        <BlInputText class="col-span-6" :input="titleInput" @changed="inputHandler" />
        <!--  -->
        <BlSelectBox class="col-span-3" label="Data collection method" asterisk v-model="typeInput"
          placeholder="Select type" :options="typeOptions" @change="typeInput = $event" />
        <BlSelectBox class="col-span-3" label="Curriculum" asterisk search v-model="curriculumIdInput"
          placeholder="Select curriculum" :options="defaultCurriculumOptions" :fetchProperty="{
            url: `/api/v1/curriculums?databank_id=${this.$route.params.id}&sort=name_asc&query=`,
            dataKey: 'curriculums',
            countKey: 'total_count',
            optionKeys: { value: 'id', label: 'name', prefixColor: 'color' }
          }" :disabled="!!defaultCurriculum" @change="curriculumIdInput = $event" />
        <!--  -->
        <BlInputText class="col-span-6" :input="descriptionInput" @changed="inputHandler" />
        <!--  -->
        <BlInputText v-if="typeInput.includes('Pir')" class="col-span-3" :input="intervalInput"
          @changed="inputHandler" />
        <BlInputText v-if="typeInput.includes('Pir')" class="col-span-3" :input="durationInput"
          @changed="inputHandler" />
        <!--  -->
        <BlInputText v-if="typeInput.includes('Percentage') || typeInput.includes('Pir')" class="col-span-3"
          :input="goalInput" @changed="inputHandler" />
        <BlInputTime v-if="typeInput.includes('Duration')" class="col-span-3" :input="timeInput"
          @changed="inputHandler" />
        <BlInputText v-if="typeInput.includes('Frequency')" class="col-span-3" :input="goalFrequencyInput"
          @changed="inputHandler" />
        <div v-if="typeInput.includes('Prompting')" class="flex flex-col col-span-6">
          <label class="relative w-full mb-1 text-sm leading-relaxed text-grey">
            {{ "Goal and success metric" }}
            <span class="absolute ml-1 text-xl text-error-400 -top-2">*</span>
          </label>
          <div class="flex flex-wrap items-center gap-1 text-sm">
            <BlTextBody2 text="Achieve target with" />
            <BlSelectBox dense v-model="successMetricInput" placeholder="Select success metric" :options="defaultPrompts.map(i => ({ value: i.name, label: i.name }))
              " @change="successMetricInput = $event" class="max-w-48" />
            <BlTextBody2 text="prompt, minimum" />
            <input type="number" v-model="goalPromptInput"
              class="w-10 h-8 px-2 text-center border rounded outline-none border-grey-200 text-grey focus:border-light-purple" />
            <BlTextBody2 text="attempt(s) per session." />
          </div>
        </div>
        <BlSelectBox v-else-if="typeInput" class="col-span-3" label="Success metric" asterisk
          v-model="successMetricInput" placeholder="Select success metric" :options="successMetricOptions"
          @change="successMetricInput = $event" />
        <BlInputText v-if="typeInput.includes('Percentage')" class="col-span-3" :input="trialsInput"
          @changed="inputHandler" />
      </div>
      <div class="flex flex-col gap-2 py-4 prompt-selection" v-if="typeInput.includes('Prompting')">
        <BlTextSubtitle1 text="Prompt selection" />
        <BlTextBody2
          text="Choose or customize prompts for this target; each symbol represents a specific prompt on the graph. You can personalize the card later, before each session, by disabling specific prompts to create a simplified card based on your preferences!" />

        <div class="flex items-center justify-center p-4 border rounded bg-grey-100 h-76 border-grey-200">
          <PromptPreview :prompts="defaultPrompts" :style="{ transform: 'scale(0.5 , 0.5)' }" />
        </div>
        <div class="flex flex-col gap-2 mx-6">
          <div class="flex items-center gap-4">
            <div class="flex-shrink-0 w-2" />
            <BlTextSubtitle4 text="Prompt name" class="w-full" />
            <BlTextSubtitle4 text="Code" class="flex-shrink-0 w-15" />
            <BlTextSubtitle4 text="Symbol" class="flex-shrink-0 w-24" />
            <div class="flex-shrink-0 w-6" />
          </div>
          <draggable handle=".handle" @update.prevent="onMovePosition($event)" class="mb-2 space-y-4">
            <div v-for="prompt in onSort(defaultPrompts, 'position')" :key="prompt.id + '-' + prompt.position"
              :id="prompt.id" class="flex items-center gap-4">
              <div class="flex items-center justify-center flex-shrink-0 w-2 text-sm cursor-move handle text-grey-400">
                <BlIcon icon="drag" class="flex-shrink-0 w-4" />
              </div>
              <PromptItem :prompt="prompt" @update="onUpdatePrompt" @delete="onRemovePrompt" :directDelete="true"
                :disabledDelete="isUsedToSuccessMetric(prompt.name)" :tooltipDelete="isUsedToSuccessMetric(prompt.name)
                  ? 'You cannot delete this prompt because it has been used as a success metric.'
                  : ''
                  " class="" />
            </div>
          </draggable>
          <div class="flex">
            <BlInputButton icon="plus" :label="'Add prompt'" @clicked="onAddPrompt" type="plain" class="px-0" />
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-2 py-4 probing" v-if="typeInput.includes('Percentage')">
        <div class="flex items-center">
          <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
            <BlInputToggle :checked="probingEnableInput" @changed="probingEnableInput = !probingEnableInput" />
          </div>
          <BlTextSubtitle1 text="Probing" />
        </div>
        <BlTextBody2
          text="Probing can be employed to assess a skill at various stages of the therapy. Activating probing gives you additional option to conduct probes during the session." />
        <div v-if="probingEnableInput" class="grid grid-cols-6 gap-4">
          <BlInputText class="col-span-3" :input="probingTrialInput" @changed="inputHandler" />
          <BlInputText class="col-span-3" :input="probingGoalInput" @changed="inputHandler" />
        </div>
      </div>
      <div class="flex flex-col gap-2 py-4 action-recommendation" v-if="typeInput">
        <div class="flex items-center gap-2">
          <div class="p-1 bg-orange-300" :style="{ borderRadius: '4px' }">
            <BlIcon icon="tooltip" class="w-5 h-5" />
          </div>
          <BlTextSubtitle1 text="Action recommendations" />
        </div>
        <BlTextBody2
          text="Below are the targets that have successfully reached their success metrics and may need further action." />
        <ActionRecommendationInput :navigateSuccessMetric="false" :totalSuccess="totalSuccessInput"
          :consecutiveSuccess="consecutiveSuccessInput" @input="onInputActionRecommendation($event)" />
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import draggable from "vuedraggable";

import PromptPreview from "@/components/repository/prompting/Preview.vue";
import PromptItem from "@/components/repository/prompting/Item.vue";
import ActionRecommendationInput from "../../clients/target/modal/ActionRecommendationInput.vue";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputTime from "../../BlInputTime.vue";
import BlBackdrop from "../../BlBackdrop.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputToggle from "../../BlInputToggle.vue";

export default {
  name: "SuperAdminNewTargetModal",
  components: {
    draggable,
    PromptPreview,
    PromptItem,
    ActionRecommendationInput,
    BlTextBody2,
    BlInputButton,
    BlInputText,
    BlInputTime,
    BlBackdrop,
    BlTextSubtitle1,
    BlSelectBox,
    BlTextSubtitle4,
    BlIcon,
    BlInputToggle
  },
  props: {
    title: { type: String, required: false, default: "Create new target" },
    description: { type: String, required: false, default: "" },
    clientId: { type: Number | String, required: false },
    kind: { type: String, required: false, default: "databank" },
    defaultCurriculum: { type: Object, required: false, default: () => null }
  },
  data() {
    return {
      submitLoading: false,
      titleInput: {
        label: "Title",
        placeholder: "Target title",
        model: "",
        asterisk: true
      },
      curriculumIdInput: "",
      typeInput: "",
      typeOptions: [
        { value: "Target::Percentage", label: "Percentage" },
        { value: "Target::Duration", label: "Duration" },
        { value: "Target::Pir", label: "Partial Interval Recording" },
        { value: "Target::Frequency", label: "Frequency" },
        { value: "Target::Prompting", label: "Prompting" }
      ],
      descriptionInput: {
        label: "Description",
        placeholder: "Target description",
        model: "",
        textarea: true
      },
      goalInput: {
        label: "Goal",
        placeholder: "1-100",
        model: "",
        suffix: "percent (%)",
        asterisk: true
      },
      goalFrequencyInput: {
        label: "Goal",
        placeholder: "Target goal",
        model: "5",
        suffix: "attempt(s) per session",
        asterisk: true
      },
      trialsInput: {
        label: "Number of trials",
        placeholder: "Target's number of trials",
        model: "",
        asterisk: true
      },
      timeInput: {
        label: "Goal time (hours/minutes/seconds)",
        model: "",
        asterisk: true
      },
      intervalInput: {
        label: "Interval",
        placeholder: "Target interval",
        model: "5",
        disabled: true,
        suffix: "Minutes"
      },
      durationInput: {
        label: "Duration",
        placeholder: "Minimum 5 minutes",
        model: "5",
        asterisk: true,
        suffix: "Minutes"
      },
      goalPromptInput: "2",
      successMetricInput: "",
      successMetricOptions: [
        {
          value: "equal to or greater than goal",
          label: "Equal to or greater than goal"
        },
        { value: "less than goal", label: "Less than goal" }
      ],
      //
      defaultPrompts: [],
      promptPageIndex: 0,
      //
      probingEnableInput: false,
      probingTrialInput: {
        label: "Minimum trials needed",
        placeholder: "Probing's minimum trials",
        model: "",
        asterisk: true,
        suffix: "trials"
      },
      probingGoalInput: {
        label: "Goal",
        placeholder: "1-100",
        model: "",
        asterisk: true,
        suffix: "percent (%)"
      },
      //
      totalSuccessChecked: true,
      totalSuccessInput: 2,
      consecutiveSuccessChecked: true,
      consecutiveSuccessInput: 2
    };
  },
  computed: {
    ...mapGetters(["centerPrompts"]),
    defaultCurriculumOptions() {
      if (!this.defaultCurriculum) return [];
      const opt = {
        value: this.defaultCurriculum.id,
        label: this.defaultCurriculum.name,
        prefixColor: this.defaultCurriculum.color
      };
      return [opt];
    },
    newTarget() {
      const target = {
        name: this.titleInput.model,
        curriculum_id: this.curriculumIdInput,
        type: this.typeInput,
        status: "pending",
        description: this.descriptionInput.model,
        success_metric: this.successMetricInput
      };
      if (target.type === "Target::Percentage") {
        target.goal = Number(this.goalInput.model || "0");
        target.number_of_trial = Number(this.trialsInput.model || "0");
        target.probing_enable = this.probingEnableInput;
        target.probing_number_of_trial = Number(
          this.probingTrialInput.model || "0"
        );
        target.probing_goal = Number(this.probingGoalInput.model || "0");
      } else if (target.type === "Target::Duration") {
        target.goal_time = this.timeInput.model;
      } else if (target.type === "Target::Pir") {
        target.goal = Number(this.goalInput.model || "0");
        target.interval = Number(this.intervalInput.model || "0");
        target.duration = Number(this.durationInput.model || "0");
      } else if (target.type === "Target::Frequency") {
        target.goal = Number(this.goalFrequencyInput.model || "0");
      } else if (target.type === "Target::Prompting") {
        target.goal = Number(this.goalPromptInput || "0");
      }
      return target;
    },
    isDisabledSubmit() {
      // Basic checks
      if (
        !this.newTarget.name ||
        !this.newTarget.type ||
        !this.newTarget.curriculum_id ||
        !this.newTarget.success_metric
      ) {
        return true;
      }
      // Type-specific checks
      if (this.newTarget.type.includes("Percentage")) {
        if (!this.newTarget.goal) return true;
        if (!this.newTarget.number_of_trial) return true;
      }
      if (this.newTarget.type.includes("Duration")) {
        if (this.newTarget.goal_time === "00:00:00") return true;
        if (!this.newTarget.goal_time) return true;
      }
      if (this.newTarget.type.includes("Pir")) {
        if (!this.newTarget.goal) return true;
        if (!this.newTarget.interval) return true;
        if (!this.newTarget.duration) return true;
      }
      if (this.newTarget.type.includes("Frequency")) {
        if (!this.newTarget.goal) return true;
      }
      if (this.newTarget.type.includes("Prompting")) {
        if (!this.newTarget.goal) return true;
      }
      // Probing for percentage
      if (this.newTarget.type.includes("Percentage")) {
        if (this.newTarget.probing_enable) {
          if (!this.newTarget.probing_number_of_trial) return true;
          if (!this.newTarget.probing_goal) return true;
        }
      }
      // Action recommendation checks
      if (this.totalSuccessChecked) {
        if (this.totalSuccessInput === "") return true;
      }
      if (this.consecutiveSuccessChecked) {
        if (this.consecutiveSuccessInput === "") return true;
      }
      return false;
    }
  },
  watch: {
    typeInput() {
      this.successMetricInput = "";
    },
    centerPrompts: {
      deep: true,
      handler(val) {
        this.generateDefaultPrompts(val);
      }
    }
  },
  async mounted() {
    // must be changes and add params for fetch prompts
    await this.getCenterPrompts();
    this.promptPageIndex = 0;
    this.onResetInputs();
    this.generateDefaultPrompts(this.centerPrompts);
    if (this.defaultCurriculum) {
      this.curriculumIdInput = this.defaultCurriculum.id;
    }
  },
  methods: {
    ...mapActions([
      "createTarget",
      "getCenterPrompts",
      "createActionRecommendation",
      "showToast"
    ]),
    ...mapMutations(["setCenterPrompts"]),
    generateDefaultPrompts(val = []) {
      if (val && val.length) {
        this.defaultPrompts = val;
      }
    },
    isUsedToSuccessMetric(promptName) {
      return promptName === this.successMetricInput;
    },
    onSort(arr = [], key = "") {
      if (!arr.length) return arr;
      if (!key) return arr;
      return arr;
    },
    onAddPrompt() {
      let length = 0;
      if (this.defaultPrompts && this.defaultPrompts.length) {
        length = this.defaultPrompts.length;
      }
      function getRandomId() {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let combination = "";
        for (let i = 0; i < 6; i++) {
          combination += chars[Math.round(Math.random() * 35)];
        }
        return `new-prompt-${combination}`;
      }
      const prompt = {
        id: getRandomId(),
        name: "New Prompt",
        abbreviation: "NP",
        shape: "square",
        color: "cherry",
        position: length + 1
      };
      const arr = [...this.defaultPrompts];
      arr.push(prompt);
      this.defaultPrompts = arr.map((i, n) => ({ ...i, position: n + 1 }));
    },
    onUpdatePrompt(item) {
      const index = this.defaultPrompts.findIndex(i => i.id === item.id);
      if (index > -1) {
        this.defaultPrompts[index] = item;
      }
    },
    onRemovePrompt(item) {
      const arr = [...this.defaultPrompts].filter(i => i.id !== item.id);
      this.defaultPrompts = arr.map((i, n) => ({ ...i, position: n + 1 }));
    },
    onMovePosition(event) {
      function arrayMove(arr, oldIndex, newIndex) {
        while (oldIndex < 0) {
          oldIndex += arr.length;
        }
        while (newIndex < 0) {
          newIndex += arr.length;
        }
        if (newIndex >= arr.length) {
          var k = newIndex - arr.length + 1;
          while (k--) {
            arr.push(undefined);
          }
        }
        arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
        return arr; // for testing
      }

      const oldIndex = event.oldIndex;
      const newIndex = event.newIndex;

      const base = [...this.defaultPrompts];
      const sorted = arrayMove(base, oldIndex, newIndex);

      this.defaultPrompts = [...sorted].map((i, n) => {
        return { ...i, position: n + 1 };
      });
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async onSubmit() {
      const params = {
        databank_id: this.$route.params.id,
        kind: this.kind,
        target: {
          name: this.newTarget.name,
          type: this.newTarget.type,
          curriculum_id: this.newTarget.curriculum_id,
          description: this.newTarget.description,
          success_metric: this.newTarget.success_metric
        }
      };

      if (params.target.type === "Target::Percentage") {
        params.target.goal = this.newTarget.goal;
        params.target.number_of_trial = this.newTarget.number_of_trial;
        params.target.probing_enable = this.newTarget.probing_enable;
        params.target.probing_number_of_trial = this.newTarget.probing_number_of_trial;
        params.target.probing_goal = this.newTarget.probing_goal;
        if (params.target.goal > 100) {
          const title = "Invalid input";
          const message = "Goal must be less than or equal to 100 percent(%).";
          this.showToast({ title, message, type: "error" });
          return;
        }
        if (params.target.probing_enable && params.target.probing_goal > 100) {
          const title = "Invalid input";
          const message =
            "Probing goal must be less than or equal to 100 percent(%).";
          this.showToast({ title, message, type: "error" });
          return;
        }
      }
      if (params.target.type === "Target::Duration") {
        params.target.goal_time = this.newTarget.goal_time;
      }
      if (params.target.type === "Target::Pir") {
        params.target.goal = this.newTarget.goal;
        params.target.interval = this.newTarget.interval;
        params.target.duration = this.newTarget.duration;
        if (params.target.goal > 100) {
          const title = "Invalid input";
          const message = "Goal must be less than or equal to 100 percent(%).";
          this.showToast({ title, message, type: "error" });
          return;
        }
        if (params.target.duration < 5) {
          const title = "Invalid input";
          const message = "Duration must be more than or equal to 5 minutes.";
          this.showToast({ title, message, type: "error" });
          return;
        }
      }
      if (params.target.type === "Target::Frequency") {
        params.target.goal = this.newTarget.goal;
      }
      if (params.target.type === "Target::Prompting") {
        params.target.goal = this.newTarget.goal;
        params.target.prompts_attributes = [...this.defaultPrompts]
          .sort((a, b) => a.position - b.position)
          .map((i, n) => {
            return {
              name: i.name,
              abbreviation: i.abbreviation,
              shape: i.shape,
              color: i.color,
              position: n + 1
            };
          });
      }

      if (this.totalSuccessChecked && this.totalSuccessInput < 1) {
        const title = "Invalid input";
        const message =
          "Please enter a value greater than or equal to 1 for total success.";
        this.showToast({ title, message, type: "error" });
        return;
      }
      if (this.consecutiveSuccessChecked && this.consecutiveSuccessInput < 2) {
        const title = "Invalid input";
        const message =
          "Please enter a value greater than or equal to 2 for consecutive.";
        this.showToast({ title, message, type: "error" });
        return;
      }

      this.submitLoading = true;
      const { success, data: newTarget } = await this.createTarget(params);
      if (!success) {
        this.submitLoading = false;
        this.$emit("close-modal");
        return;
      }

      const paramsRecommendation = {
        kind: this.kind,
        target_id: newTarget.id,
        action_recommendation: {
          target_id: newTarget.id,
          total_success: this.totalSuccessChecked
            ? this.totalSuccessInput
            : null,
          consecutive_success: this.consecutiveSuccessChecked
            ? this.consecutiveSuccessInput
            : null,
          recommended_action: "mastered"
        }
      };

      this.submitLoading = true;
      await this.createActionRecommendation(paramsRecommendation);
      this.submitLoading = false;

      const title = "New target created";
      const message = "Success! New target has been created successfully.";
      this.showToast({ title, message, type: "success" });
      this.$emit("close-modal");
    },
    onResetInputs() {
      this.titleInput.model = "";
      this.descriptionInput.model = "";
      this.curriculumIdInput = "";
      this.typeInput = "";
      this.goalInput.model = "";
      this.goalFrequencyInput.model = "5";
      this.trialsInput.model = "";
      this.timeInput.model = "";
      this.intervalInput.model = "5";
      this.durationInput.model = "";
      this.successMetricInput = "";
      this.goalPromptInput = "2";
    },
    onInputActionRecommendation(val) {
      this.totalSuccessChecked = val.total_success_checked;
      this.totalSuccessInput = val.total_success;
      this.consecutiveSuccessChecked = val.consecutive_success_checked;
      this.consecutiveSuccessInput = val.consecutive_success;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/databank/NewTargetModal.vue